import { useServiceStages, isTerminalStage } from '@/api/useServiceStages';
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  StageBadge,
} from '@/components/AppShell';
import { getStageLabelText, STAGE_TONE } from '@/utils/stageLabels';
import { formatDateTime } from '@/utils/formatDate';

/**
 * 服务进度页（Issue #198）。
 *
 * 展示后端 me/service-stages 的时间轴；不展示任何后端未下发的字段。
 * 进度本身是只读视图，客户端不进行阶段推进或写操作。
 */
export function ServiceProgressPage() {
  const { data, loading, notFound, error, refetch } = useServiceStages();

  return (
    <div>
      <PageHeader
        title="服务进度"
        description="查看您的服务阶段、关键节点与下一步提示。"
      />

      {error ? <ErrorBanner message={error.message} onRetry={refetch} /> : null}

      {loading ? (
        <Card>
          <p className="text-sm text-muted-foreground">加载服务进度…</p>
        </Card>
      ) : notFound ? (
        <Card>
          <EmptyState
            title="您的服务档案尚未建立"
            description="请联系您的专属顾问开通服务档案后再回到本页面。"
          />
        </Card>
      ) : data ? (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">当前阶段</p>
                <p className="mt-1 text-base font-semibold">
                  {getStageLabelText(data.currentStage)}
                </p>
              </div>
              <StageBadge
                label={isTerminalStage(data.currentStage) ? '终态' : '进行中'}
                tone={STAGE_TONE[data.currentStage]}
              />
            </div>
            {data.nextStepHint && !isTerminalStage(data.currentStage) ? (
              <p className="mt-3 rounded-md bg-primary/5 p-3 text-sm text-primary">
                下一步：{data.nextStepHint}
              </p>
            ) : null}
            <p className="mt-3 text-xs text-muted-foreground">
              最近更新 {formatDateTime(data.updatedAt)}
            </p>
          </Card>

          <Card>
            <h2 className="text-sm font-semibold">阶段时间轴</h2>
            {data.timeline.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">暂无阶段变更记录。</p>
            ) : (
              <ol className="mt-3 space-y-3">
                {data.timeline.map((event) => (
                  <li
                    key={event.id}
                    className="flex items-start gap-3 border-l-2 border-primary/40 pl-3"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {getStageLabelText(event.fromStage)} →{' '}
                        {getStageLabelText(event.toStage)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(event.createdAt)}
                      </p>
                      {event.reason ? (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {event.reason}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
}
